// src/components/sign/SignaturePad.js
// Branded signature capture — draw with finger/mouse OR type with cursive font.
// Outputs a base64 PNG via onChange so it can be stored in Firestore as the
// signed signature image.
import React, { useEffect, useRef, useState } from 'react';

const BURG = '#6B1E2E';
const BURG_DK = '#4A1220';
const GOLD = '#C9A84C';
const CREAM = '#FAF7F2';
const BORDER = 'rgba(107,30,46,0.15)';
const INK = '#1A1A2E';

export default function SignaturePad({ defaultName = '', onChange }) {
  const [mode, setMode] = useState('draw');
  const [typedName, setTypedName] = useState(defaultName);
  const [hasDrawn, setHasDrawn] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);

  // Set up the drawing canvas with proper device-pixel-ratio scaling so the
  // line stays crisp on retina displays.
  useEffect(() => {
    if (mode !== 'draw') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = INK;
  }, [mode]);

  // Render typed signature → image whenever the typed name changes
  useEffect(() => {
    if (mode !== 'type') return;
    if (!typedName.trim()) { onChange(''); return; }
    const c = document.createElement('canvas');
    c.width = 720; c.height = 180;
    const ctx = c.getContext('2d');
    ctx.fillStyle = INK;
    ctx.font = '64px "Great Vibes", "Sacramento", "Brush Script MT", cursive';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillText(typedName.trim(), c.width / 2, c.height / 2);
    onChange(c.toDataURL('image/png'));
  }, [mode, typedName, onChange]);

  // Drawing handlers
  const start = (e) => {
    drawing.current = true;
    lastPoint.current = getPoint(e, canvasRef.current);
    e.preventDefault();
  };
  const move = (e) => {
    if (!drawing.current) return;
    const point = getPoint(e, canvasRef.current);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPoint.current = point;
    setHasDrawn(true);
    e.preventDefault();
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL('image/png'));
  };
  const clearDraw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onChange('');
  };

  return (
    <div>
      <div style={s.tabs} role="tablist">
        <button onClick={() => setMode('draw')} type="button" style={mode === 'draw' ? { ...s.tab, ...s.tabActive } : s.tab}>
          Draw signature
        </button>
        <button onClick={() => setMode('type')} type="button" style={mode === 'type' ? { ...s.tab, ...s.tabActive } : s.tab}>
          Type signature
        </button>
      </div>

      {mode === 'draw' ? (
        <>
          <div style={s.padFrame}>
            <canvas
              ref={canvasRef}
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              style={s.padCanvas}
            />
            {!hasDrawn && <div style={s.padHint}>Sign here with your finger or mouse</div>}
            <div style={s.padBaseline} />
          </div>
          <div style={s.row}>
            <span style={s.helper}>Drawn signatures are captured as images and embedded in the signed PDF.</span>
            {hasDrawn && <button onClick={clearDraw} type="button" style={s.clear}>Clear</button>}
          </div>
        </>
      ) : (
        <>
          <input
            value={typedName}
            onChange={e => setTypedName(e.target.value)}
            placeholder="Type your full legal name"
            style={s.typeInput}
          />
          <div style={s.typedFrame}>
            {typedName.trim() ? (
              <span style={s.typedSignature}>{typedName}</span>
            ) : (
              <span style={s.padHintCenter}>Your typed signature will preview here</span>
            )}
            <div style={s.padBaseline} />
          </div>
          <div style={s.helper}>By typing your name, you adopt this signature as your electronic mark.</div>
        </>
      )}
    </div>
  );
}

function getPoint(e, canvas) {
  const rect = canvas.getBoundingClientRect();
  const t = e.touches && e.touches[0] ? e.touches[0] : e;
  return { x: t.clientX - rect.left, y: t.clientY - rect.top };
}

const s = {
  tabs: { display: 'inline-flex', background: CREAM, border: `1px solid ${BORDER}`, borderRadius: 999, padding: 3, marginBottom: 14 },
  tab: { padding: '7px 18px', background: 'transparent', border: 'none', fontSize: 12, fontWeight: 500, color: '#5A5A5A', cursor: 'pointer', borderRadius: 999, fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em' },
  tabActive: { background: '#fff', color: BURG_DK, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  padFrame: { position: 'relative', background: '#fff', border: `1px dashed ${GOLD}`, borderRadius: 6, height: 180, overflow: 'hidden' },
  padCanvas: { width: '100%', height: '100%', display: 'block', cursor: 'crosshair', touchAction: 'none' },
  padHint: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#B7B0A6', fontSize: 13, fontStyle: 'italic', pointerEvents: 'none', fontFamily: "'DM Sans', sans-serif" },
  padHintCenter: { color: '#B7B0A6', fontSize: 13, fontStyle: 'italic', fontFamily: "'DM Sans', sans-serif" },
  padBaseline: { position: 'absolute', left: 16, right: 16, bottom: 24, height: 0, borderBottom: '1px solid #D6CFC2' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 },
  helper: { fontSize: 11, color: '#9A9A9A', fontStyle: 'italic', fontFamily: "'DM Sans', sans-serif" },
  clear: { background: 'transparent', border: `1px solid ${BORDER}`, color: BURG, fontSize: 11, padding: '5px 12px', borderRadius: 4, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  typeInput: { width: '100%', padding: '12px 14px', border: `1px solid ${BORDER}`, borderRadius: 4, fontSize: 15, fontFamily: "'DM Sans', sans-serif", marginBottom: 14, color: BURG_DK, outline: 'none' },
  typedFrame: { position: 'relative', background: '#fff', border: `1px dashed ${GOLD}`, borderRadius: 6, height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  typedSignature: { fontFamily: '"Great Vibes", "Sacramento", "Brush Script MT", cursive', fontSize: 56, color: INK, lineHeight: 1.1 }
};
